import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ITag } from 'services/api/entities';
import { tagActionNames } from 'services/store/tags/actions';
import { checkLoadingSelector } from 'services/store/base/selectors';
import type { BaseActionStore, StoreRootState } from 'services/store/types';
import useTagsStore from '../tags';

const useUrlTagsParameters = (): void => {
  const { tagsStore, selectTagsFromURL } = useTagsStore();
  const { selectedTags } = tagsStore;
  const { search } = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  const baseStore: BaseActionStore = useSelector(
    (store: StoreRootState) => store.base
  );

  const isTagSelectionLoading = checkLoadingSelector(baseStore, [
    tagActionNames.TAG_SELECTION_FROM_URL,
    tagActionNames.TAG_GET_LIST,
  ]);

  useEffect(() => {
    setIsLoading(isTagSelectionLoading);
  }, [isTagSelectionLoading]);

  const query = useMemo(() => new URLSearchParams(search), [search]);

  const paramsFromUrl = useMemo(() => {
    const paramsFromUrlSet = new Set(query.keys());
    return Array.from(paramsFromUrlSet);
  }, [query]);

  // --- SELECTING FILTERS WHEN ACCESSING PAGE ACTIONS ---

  // Fetch all tags from url
  useEffect(() => {
    const tags: Record<string, Array<string>> = {
      moods: [],
      genres: [],
      vocals: [],
      instruments: [],
      keys: [],
      energies: [],
      eras: [],
    };

    // Fetch tags by keys and set parameters in use
    if (paramsFromUrl.length) {
      paramsFromUrl.forEach((parameter) => {
        const parameters = query
          .getAll(parameter)
          .map((param) => param.replace('sharp', '#'));

        if (parameters.length) {
          tags[parameter] = parameters;
        }
      });
      selectTagsFromURL(tags);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsFromUrl, query]);

  // converts selected tags into url parameters in efficient way
  const paramsToString = useCallback(() => {
    const urlParams = ['?'];
    Object.keys(selectedTags).forEach((tagType: string) => {
      selectedTags[tagType].forEach((tag: ITag) => {
        const transformedSlug = tag.slug.replace('#', 'sharp');
        urlParams.push(`${tagType}=${transformedSlug}&`);
      });
    });

    const urlString = urlParams.join('');
    // This also handles empty search params
    return urlString.slice(0, -1);
  }, [selectedTags]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    const newPath = paramsToString();
    navigate(
      {
        search: newPath,
      },
      { replace: true }
    );
  }, [isLoading, paramsFromUrl, paramsToString, navigate]);
};

export default useUrlTagsParameters;
